import React, { useEffect, useState, useRef } from 'react';
import { Box, Paper, Typography, Button, LinearProgress, IconButton, Stack, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card, CardActionArea, CardContent, CardActions, Tooltip, Avatar, Chip, Skeleton, Checkbox, FormControl, InputLabel, Select, MenuItem, TextField, ToggleButton, ToggleButtonGroup, List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction, Divider } from '@mui/material';
import { Upload as UploadIcon, Delete as DeleteIcon, Download as DownloadIcon, Visibility as VisibilityIcon, PictureAsPdf as PdfIcon, Image as ImageIcon, Description as DocIcon, Article as TextIcon, InsertDriveFile as FileIcon, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { XMLParser } from 'fast-xml-parser';
import * as fileService from '../../services/fileService';

const DropZone = styled(Paper)(({ theme, isdragging }) => ({
  padding: theme.spacing(3),
  border: `2px dashed ${theme.palette.divider}`,
  background: theme.palette.mode === 'dark' ? '#1e2124' : '#fafafa',
  textAlign: 'center',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  outline: 'none',
  ...(isdragging === 'true' && {
    borderColor: theme.palette.primary.main,
    background: theme.palette.mode === 'dark' ? '#19202a' : '#f0f7ff'
  })
}));

export default function ProjectFilesTab({ projectId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState({ type: null, title: '', url: '', text: '' });
  const [selected, setSelected] = useState(new Set());
  const [sortField, setSortField] = useState('date'); // 'date' | 'size' | 'name' | 'type'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [filterKind, setFilterKind] = useState('all'); // 'all' | 'image' | 'pdf' | 'text' | 'docx' | 'file'
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [displayNames, setDisplayNames] = useState({}); // id -> display name (client-only)
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [query, setQuery] = useState('');

  const formatSize = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const ids = (files || []).map(f => f._id || f.id || f.name || f.filename).filter(Boolean);
    setSelected(new Set(ids));
  };

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    for (const id of ids) {
      await onDelete(id);
    }
    setSelected(new Set());
  };

  const bulkDownload = async () => {
    for (const id of selected) {
      const f = (files || []).find(x => (x._id || x.id || x.name || x.filename) === id);
      if (f) await onDownload(id, f.name || f.filename);
    }
  };

  const startRename = (f) => {
    const id = f._id || f.id || f.name || f.filename;
    setRenamingId(id);
    setRenameValue(displayNames[id] || f.name || f.filename || '');
  };
  const commitRename = (f) => {
    const id = f._id || f.id || f.name || f.filename;
    const value = (renameValue || '').trim();
    if (value) setDisplayNames(prev => ({ ...prev, [id]: value }));
    setRenamingId(null);
    setRenameValue('');
  };
  const cancelRename = () => { setRenamingId(null); setRenameValue(''); };

  const handleKeyDownWrapper = (e) => {
    if (e.key === 'Delete' && selected.size > 0) {
      e.preventDefault();
      bulkDelete();
    }
    if (e.ctrlKey && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      selectAll();
    }
  };

  const applyFilterSort = (arr) => {
    let out = [...(arr || [])];
    // text search
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter(f => {
        const id = f._id || f.id || f.name || f.filename;
        const name = (displayNames[id] || f.name || f.filename || '').toLowerCase();
        return name.includes(q);
      });
    }
    if (filterKind !== 'all') {
      out = out.filter(f => fileTypeMeta(f).kind === filterKind);
    }
    out.sort((a, b) => {
      const ka = a._id || a.id || a.name || a.filename;
      const kb = b._id || b.id || b.name || b.filename;
      let va, vb;
      switch (sortField) {
        case 'size': va = a.size || 0; vb = b.size || 0; break;
        case 'name': va = (displayNames[ka] || a.name || a.filename || '').toLowerCase(); vb = (displayNames[kb] || b.name || b.filename || '').toLowerCase(); break;
        case 'type': va = fileTypeMeta(a).kind; vb = fileTypeMeta(b).kind; break;
        default: va = new Date(a.createdAt || a.uploadedAt || 0).getTime(); vb = new Date(b.createdAt || b.uploadedAt || 0).getTime();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return out;
  };

  const getExt = (name = '') => {
    const n = name.toLowerCase();
    const m = /\.([a-z0-9]+)$/.exec(n);
    return m ? m[1] : '';
  };

  const fileTypeMeta = (f) => {
    const name = (f.name || f.filename || '').toLowerCase();
    const ext = getExt(name);
    if (['png','jpg','jpeg','gif','webp','tif','tiff'].includes(ext)) return { kind: 'image', icon: <ImageIcon />, color: 'info' };
    if (ext === 'pdf') return { kind: 'pdf', icon: <PdfIcon />, color: 'error' };
    if (['txt','md','log','csv','json','xml','yaml','yml','rtf'].includes(ext)) return { kind: 'text', icon: <TextIcon />, color: 'primary' };
    if (ext === 'docx') return { kind: 'docx', icon: <DocIcon />, color: 'secondary' };
    return { kind: 'file', icon: <FileIcon />, color: 'default' };
  };

  const refresh = async () => {
    try {
      setLoading(true);
      const list = await fileService.listFiles(projectId);
      setFiles(list || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load files');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!projectId) return;
    refresh();
    // restore display names
    try {
      const saved = JSON.parse(localStorage.getItem(`project:${projectId}:fileDisplayNames`) || '{}');
      setDisplayNames(saved || {});
    } catch {}
  }, [projectId]);

  useEffect(() => {
    try { localStorage.setItem(`project:${projectId}:fileDisplayNames`, JSON.stringify(displayNames)); } catch {}
  }, [projectId, displayNames]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const uploaded = await fileService.uploadFiles(projectId, Array.from(fileList), (p) => setProgress(p));
      // Optimistically merge: keep all previous items and add new ones, unique by id/filename
      setFiles((prev) => {
        const merged = [...(prev || []), ...(uploaded || [])];
        const seen = new Set();
        const out = [];
        for (const f of merged) {
          const key = f.id || f._id || f.name || f.filename;
          if (!key || seen.has(key)) continue;
          seen.add(key);
          out.push(f);
        }
        return out;
      });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const onPick = () => inputRef.current?.click();

  const onChangeInput = (e) => {
    handleFiles(e.target.files);
    // allow selecting the same file again by resetting the input value
    e.target.value = '';
  };

  const onDelete = async (fileId) => {
    try {
      await fileService.deleteFile(projectId, fileId);
      await refresh();
      setSelected(prev => { const next = new Set(prev); next.delete(fileId); return next; });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const onDownload = async (fileId, name) => {
    try {
      const blob = await fileService.downloadFile(projectId, fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Download failed');
    }
  };

  const onPreview = async (f) => {
    try {
      const name = (f.name || f.filename || '').toLowerCase();
      const fileId = f._id || f.id || f.name || f.filename;
      const blob = await fileService.downloadFile(projectId, fileId);
      // Prefer extension; fallback to blob type
      const isPdf = name.endsWith('.pdf') || blob.type === 'application/pdf';
      const isText = name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.log') || name.endsWith('.csv') || name.endsWith('.json') || name.endsWith('.xml') || name.endsWith('.yaml') || name.endsWith('.yml') || name.endsWith('.rtf') || (blob.type && blob.type.startsWith('text/'));
      const isImage = name.match(/\.(png|jpe?g|gif|webp|tif?f)$/) || (blob.type && blob.type.startsWith('image/'));
      const isDocx = name.endsWith('.docx') || blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (isPdf) {
        if (!blob || blob.size === 0) {
          setError('The file preview is empty. Try downloading instead.');
          return;
        }
        const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        setPreview({ type: 'pdf', title: f.name || f.filename, url, text: '' });
        setPreviewOpen(true);
        return;
      }
      if (isText) {
        const raw = await blob.text();
        let text = raw;
        try {
          if (name.endsWith('.json')) {
            text = JSON.stringify(JSON.parse(raw), null, 2);
          } else if (name.endsWith('.xml')) {
            const parser = new XMLParser({ ignoreAttributes: false, attributeNamePrefix: '@_' });
            const parsed = parser.parse(raw);
            text = JSON.stringify(parsed, null, 2);
          }
        } catch (e) {
          // Fallback to raw text if pretty printing fails
          text = raw;
        }
        setPreview({ type: 'text', title: f.name || f.filename, url: '', text });
        setPreviewOpen(true);
        return;
      }
      if (isDocx) {
        try {
          const ab = await blob.arrayBuffer();
          // Dynamic import to avoid hard dependency if not installed
          const mammoth = await import(/* webpackChunkName: "mammoth" */ 'mammoth/mammoth.browser.min.js');
          const result = await mammoth.convertToHtml({ arrayBuffer: ab });
          const html = result.value || '<p>(Empty document)</p>';
          setPreview({ type: 'html', title: f.name || f.filename, url: '', text: html });
          setPreviewOpen(true);
          return;
        } catch (e) {
          setError('DOCX preview requires an additional viewer. Please download the file or install docx preview support.');
          return;
        }
      }
      if (isImage) {
        if (!blob || blob.size === 0) {
          setError('The image preview is empty. Try downloading instead.');
          return;
        }
        const url = URL.createObjectURL(blob);
        setPreview({ type: 'image', title: f.name || f.filename, url, text: '' });
        setPreviewOpen(true);
        return;
      }
      setError('Preview supported for PDF, images, text, JSON, XML, and DOCX (if viewer available). Use Download for other types.');
    } catch (e) {
      setError('Failed to preview file');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <DropZone
        isdragging={dragging ? 'true' : 'false'}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={onPick}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Upload project files</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          Drag & drop files here, or click to browse
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<UploadIcon />} 
          onClick={(ev) => { ev.stopPropagation(); onPick(); }} 
          disabled={uploading}
        >
          Select Files
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={onChangeInput}
          style={{ display: 'none' }}
          accept=".pdf,.txt,.doc,.docx,.xls,.xlsx,.csv,image/*,application/pdf"
        />
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{Math.round(progress)}%</Typography>
          </Box>
        )}
      </DropZone>

      <Paper sx={{ mt: 3, p: 2 }} tabIndex={0} onKeyDown={handleKeyDownWrapper}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Files</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField size="small" placeholder="Search files" value={query} onChange={(e)=>setQuery(e.target.value)} sx={{ minWidth: 220 }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="filter-kind-label">Filter</InputLabel>
              <Select labelId="filter-kind-label" label="Filter" value={filterKind} onChange={(e)=>setFilterKind(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="docx">DOCX</MenuItem>
                <MenuItem value="file">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="sort-field-label">Sort by</InputLabel>
              <Select labelId="sort-field-label" label="Sort by" value={sortField} onChange={(e)=>setSortField(e.target.value)}>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="size">Size</MenuItem>
                <MenuItem value="type">Type</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="sort-dir-label">Order</InputLabel>
              <Select labelId="sort-dir-label" label="Order" value={sortDir} onChange={(e)=>setSortDir(e.target.value)}>
                <MenuItem value="asc">Asc</MenuItem>
                <MenuItem value="desc">Desc</MenuItem>
              </Select>
            </FormControl>
            <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(e, v)=>{ if(v) setViewMode(v); }}>
              <ToggleButton value="grid" aria-label="grid view">Grid</ToggleButton>
              <ToggleButton value="list" aria-label="list view">List</ToggleButton>
            </ToggleButtonGroup>
            <Chip size="small" label={`${(files || []).length} ${(files || []).length === 1 ? 'item' : 'items'}`} />
          </Box>
        </Box>
        {selected.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="caption" color="text.secondary">{selected.size} selected</Typography>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={bulkDownload}>Download Selected</Button>
            <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={bulkDelete}>Delete Selected</Button>
            <Button size="small" onClick={selectAll}>Select All</Button>
            <Button size="small" onClick={clearSelection}>Clear</Button>
          </Box>
        )}
        {loading ? (
          <Grid container spacing={2}>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card sx={{ borderRadius: 1, overflow: 'hidden' }}>
                  <Skeleton variant="rectangular" height={140} />
                  <Box sx={{ p: 1.5 }}>
                    <Skeleton variant="text" width="80%" height={20} />
                    <Skeleton variant="text" width="60%" height={16} />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (files || []).length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2" sx={{ mb: 1 }}>No files uploaded yet</Typography>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={onPick}>Upload Files</Button>
          </Box>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {applyFilterSort(files || []).map((f) => {
              const meta = fileTypeMeta(f);
              const isImage = meta.kind === 'image';
              const id = f._id || f.id || f.name;
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={f._id || f.id || f.name}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      flexDirection: 'column',
                      outline: (theme) => selected.has(f._id || f.id || f.name || f.filename) ? `2px solid ${theme.palette.primary.main}` : 'none',
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      },
                      '&:focus-visible': {
                        outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2
                      }
                    }}
                  >
                    <Tooltip title="Preview">
                      <CardActionArea onClick={() => onPreview(f)} sx={{ position: 'relative' }}>
                      <Box sx={{
                        height: 140,
                        background: (theme) => theme.palette.mode === 'dark' ? '#161a1d' : '#f5f7fb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, bgcolor: 'background.paper', borderRadius: 1 }} onClick={(e)=>{ e.stopPropagation(); }}>
                          <Checkbox size="small" checked={selected.has(f._id || f.id || f.name || f.filename)} onChange={()=>toggleSelect(f._id || f.id || f.name || f.filename)} />
                        </Box>
                        {isImage ? (
                          <img
                            src={`/uploads/files/${projectId}/${encodeURIComponent(f.name || f.filename || f.id)}`}
                            alt={f.name || f.filename}
                            style={{ maxWidth: '100%', maxHeight: '100%' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <Avatar sx={{ width: 64, height: 64, bgcolor: 'background.paper', boxShadow: 1 }}>
                            {meta.icon}
                          </Avatar>
                        )}
                      </Box>
                      </CardActionArea>
                    </Tooltip>
                    <CardContent sx={{ py: 1.5, flexGrow: 1 }}>
                      {renamingId === (f._id || f.id || f.name || f.filename) ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TextField size="small" value={renameValue} onChange={(e)=>setRenameValue(e.target.value)} sx={{ flex: 1 }} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); commitRename(f); } if(e.key==='Escape'){ e.preventDefault(); cancelRename(); } }} />
                          <IconButton size="small" onClick={()=>commitRename(f)} aria-label="save name"><CheckIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={cancelRename} aria-label="cancel rename"><CloseIcon fontSize="small" /></IconButton>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                          <Tooltip title={displayNames[f._id || f.id || f.name || f.filename] || f.name || f.filename} placement="top">
                            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, flex: 1 }}>
                              {displayNames[f._id || f.id || f.name || f.filename] || f.name || f.filename}
                            </Typography>
                          </Tooltip>
                          <IconButton size="small" onClick={()=>startRename(f)} aria-label="rename file"><EditIcon fontSize="small" /></IconButton>
                        </Stack>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatSize(f.size)} • {new Date(f.createdAt || f.uploadedAt || Date.now()).toLocaleDateString()}
                        </Typography>
                        <Chip size="small" label={(meta.kind || 'file').replace(/\b\w/g, c => c.toUpperCase())} color={meta.color === 'default' ? 'default' : meta.color} variant="outlined" />
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0, pb: 1 }}>
                      <Tooltip title="Preview">
                        <IconButton aria-label="preview file" size="small" onClick={() => onPreview(f)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton aria-label="download file" size="small" onClick={() => onDownload(f._id || f.id, f.name || f.filename)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton aria-label="delete file" size="small" onClick={() => onDelete(f._id || f.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <List dense>
            {applyFilterSort(files || []).map((f, idx) => {
              const meta = fileTypeMeta(f);
              const nameId = f._id || f.id || f.name || f.filename;
              return (
                <React.Fragment key={nameId}>
                  <ListItem sx={{ '&:hover': { backgroundColor: (theme)=> theme.palette.action.hover } }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Preview"><IconButton edge="end" size="small" onClick={()=>onPreview(f)} aria-label="preview file"><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Download"><IconButton edge="end" size="small" onClick={()=>onDownload(nameId, f.name || f.filename)} aria-label="download file"><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton edge="end" size="small" onClick={()=>onDelete(nameId)} aria-label="delete file"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Rename"><IconButton edge="end" size="small" onClick={()=>startRename(f)} aria-label="rename file"><EditIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    }
                  >
                    <Checkbox size="small" edge="start" checked={selected.has(nameId)} onChange={()=>toggleSelect(nameId)} tabIndex={-1} sx={{ mr: 1 }} />
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'background.paper' }}>{meta.icon}</Avatar>
                    </ListItemAvatar>
                    {renamingId === nameId ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <TextField size="small" value={renameValue} onChange={(e)=>setRenameValue(e.target.value)} sx={{ flex: 1 }} onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); commitRename(f); } if(e.key==='Escape'){ e.preventDefault(); cancelRename(); } }} />
                        <IconButton size="small" onClick={()=>commitRename(f)} aria-label="save name"><CheckIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={cancelRename} aria-label="cancel rename"><CloseIcon fontSize="small" /></IconButton>
                      </Stack>
                    ) : (
                      <ListItemText
                        primary={displayNames[nameId] || f.name || f.filename}
                        secondary={`${formatSize(f.size)} • ${new Date(f.createdAt || f.uploadedAt || Date.now()).toLocaleDateString()} • ${fileTypeMeta(f).kind}`}
                        primaryTypographyProps={{ noWrap: true, fontWeight: 600 }}
                        secondaryTypographyProps={{ color: 'text.secondary' }}
                      />
                    )}
                  </ListItem>
                  {idx < (files?.length || 0) - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => {
          if (preview.type === 'pdf' && preview.url) {
            try { URL.revokeObjectURL(preview.url); } catch {}
          }
          setPreviewOpen(false);
          setPreview({ type: null, title: '', url: '', text: '' });
        }} 
        fullWidth 
        maxWidth={preview.type === 'pdf' ? 'lg' : 'md'}
      >
        <DialogTitle>{preview.title || 'Preview'}</DialogTitle>
        <DialogContent dividers>
          {preview.type === 'pdf' && (
            <Box sx={{ height: '75vh' }}>
              <iframe title="pdf-preview" src={preview.url} style={{ width: '100%', height: '100%', border: 0 }} />
            </Box>
          )}
          {preview.type === 'image' && (
            <Box sx={{ maxHeight: '75vh', textAlign: 'center' }}>
              <img src={preview.url} alt={preview.title} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
            </Box>
          )}
          {preview.type === 'text' && (
            <Box sx={{ maxHeight: '70vh', overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 14 }}>
              {preview.text}
            </Box>
          )}
          {preview.type === 'html' && (
            <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <div dangerouslySetInnerHTML={{ __html: preview.text }} />
            </Box>
          )}
          {!preview.type && (
            <Typography variant="body2" color="text.secondary">No preview</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {(preview.type === 'pdf' || preview.type === 'image') && preview.url && (
            <Button onClick={() => { window.open(preview.url, '_blank', 'noopener,noreferrer'); }}>Open in new tab</Button>
          )}
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
